import type { CreateAccountIdAutocompleteOptionsInputAccount } from 'components/portfolio/account/AccountService';
import type { SubfundAutocompleteOptionInput } from 'components/portfolio/fund/SubFundService';
import { IPortfolioDefinitionSubType, IPortfolioDefinitionType } from 'generated/graphql';
import { memo, type ReactElement } from 'react';
import { calculateItemProps } from './PortfolioDefCards.utils';
import GridCard from 'components/technical/GCard/GridCard';
import { PortfolioDescriptionText } from './PortfolioDescriptionText';
import { Stack, Tooltip, Typography, useTheme } from '@mui/joy';
import { AssetIconList } from 'components/market/asset/AssetIconList';
import PortfolioCreatedAtText from './PortfolioCreatedAt';
import PortfolioDefinitionActions, { type PortfolioDefActionDef } from './PortfolioDefinitionActions';
import { PortfolioDefTile } from './PortfolioDefTile';
import { VenueIconList } from 'components/venue/VenueIconList';
import truncate from 'lodash/fp/truncate';
import { maxLength } from './PortfolioDescriptionText';
import wordConverter from 'number-to-words';
import { capitalize } from 'lodash/fp';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { PortfolioStatusIndicators } from './PortfolioStatusIndicators.tsx';
import PortfolioTagsChips from './PortfolioTagsChips';

export const gridWidth = { sm: 12, md: 6, lg: 3 };

export const calculatePortfolioFlags = (
  def: PortfolioDefActionDef
): { supportsRules: boolean; showNoRulesWarning: boolean } => {
  const supportsRules =
    def.type === IPortfolioDefinitionType.Rebalanced &&
    [
      IPortfolioDefinitionSubType.Default,
      IPortfolioDefinitionSubType.MultifactorBased,
      IPortfolioDefinitionSubType.OptimizerBased,
    ].includes(def.subType);
  const showNoRulesWarning = supportsRules && def.rebalancedDetails?.rebalancingRules.length === 0;
  return { supportsRules, showNoRulesWarning };
};

const PortfolioCard = ({
  cardHeight,
  selected,
  def,
  accounts,
  subFunds,
  allTags,
}: {
  def: PortfolioDefActionDef;
  accounts: CreateAccountIdAutocompleteOptionsInputAccount[];
  subFunds: SubfundAutocompleteOptionInput[];
  cardHeight: string;
  selected: boolean;
  allTags: string[];
}): ReactElement => {
  const theme = useTheme();
  const dropdown = def.genie ? undefined : (
    <PortfolioDefinitionActions def={def} accounts={accounts} subFunds={subFunds} allTags={allTags} />
  );

  const { supportsRules, showNoRulesWarning } = calculatePortfolioFlags(def);
  const rebalancingRulesCount = def.rebalancedDetails?.rebalancingRules.length ?? 0;

  const statusAndTags = (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" mt={-0.5}>
      <PortfolioStatusIndicators def={def} />
      <PortfolioTagsChips tags={def.tags} />
    </Stack>
  );

  const portfolio =
    def.type === IPortfolioDefinitionType.Real ? (
      <PortfolioDefTile name={def.name} dropdown={dropdown}>
        {statusAndTags}
        <VenueIconList maxItems={6} accounts={def.realDefAccounts} />
        {def.realDefSubFunds.length > 0 && (
          <Typography level={'body-sm'}>
            {truncate(
              {
                length: maxLength,
              },
              def.realDefSubFunds.map((fund) => fund.name).join(', ')
            )}
          </Typography>
        )}
        <PortfolioDescriptionText definition={def} />

        <Stack direction="column" mt="auto">
          <PortfolioCreatedAtText createdAt={def.createdAt} />
        </Stack>
      </PortfolioDefTile>
    ) : (
      <PortfolioDefTile
        name={
          <>
            {def.name}
            {showNoRulesWarning && (
              <Tooltip title="No rebalancing rules">
                <WarningAmberIcon
                  sx={{ verticalAlign: 'middle', fontSize: '0.95em', color: theme.palette.danger[500], ml: '0.25rem' }}
                />
              </Tooltip>
            )}
          </>
        }
        dropdown={dropdown}
      >
        {statusAndTags}
        <AssetIconList assets={def.assets ?? []} maxItems={6} />
        <PortfolioDescriptionText definition={def} />
        <Stack direction="column" mt="auto">
          {def.subType === 'TRANSACTION_BASED' && (
            <Typography level="body-sm">Transaction tag: {def.rebalancedDetails?.transactionTag}</Typography>
          )}
          {supportsRules && (
            <Typography level="body-sm">
              {rebalancingRulesCount === 0
                ? 'No reblancing rules'
                : capitalize(wordConverter.toWords(rebalancingRulesCount)) +
                  ' rebalancing rule' +
                  (rebalancingRulesCount > 1 ? 's' : '')}
            </Typography>
          )}
          {!def.genie && <PortfolioCreatedAtText createdAt={def.createdAt} />}
        </Stack>
      </PortfolioDefTile>
    );

  return (
    <GridCard
      key={def.id}
      height={cardHeight}
      gridWidth={gridWidth}
      {...calculateItemProps({ selected, showNoRulesWarning, defId: def.id })}
    >
      {portfolio}
    </GridCard>
  );
};

export default memo(PortfolioCard);
